import { Injectable, isDevMode } from '@angular/core';
import type { StateContext } from '@ngxs/store';
import { Destroyable } from '@technocom/common';
import type { QueryParamsLike } from '@technocom/common/data';
import { DataUtils, QueryParams } from '@technocom/common/data';
import type { Type } from '@technocom/common/platform';
import type { EntityRepositoryService, EntityRequestOverrides } from '@technocom/data';
import { EntityProvider } from '@technocom/data';
import type { Entity, EntityKeyOf, EntityType, EntityTypeOf } from '@technocom/data/core';
import { EntityUtils } from '@technocom/data/core';
import { produce } from 'immer';
import * as _ from 'lodash';
import type { Observable } from 'rxjs';
import { map, of, tap } from 'rxjs';

@Injectable()
export abstract class AbstractState<TData extends object> extends Destroyable {
  constructor(protected entityProvider: EntityProvider) {
    super();
  }

  protected of<T extends Entity>(modelClass: Type<T>): EntityRepositoryService<EntityKeyOf<T>, T> {
    return this.entityProvider.getRepository(modelClass as EntityTypeOf<T>) as EntityRepositoryService;
  }

  /**
   * Делает запрос на получение модели с указанным ID.
   *
   * Сохраняет полученный объект в {@link ctx} по ключу {@link key}`[ID]`.
   *
   * Если задан {@link url} запроса и значение оканчивается идентификатором модели, то параметр
   * {@link EntityRequestOverrides.urlRoot} по умолчанию устанавливается равным `true`.
   */
  protected fetch<T extends Entity>(
    modelClass: EntityTypeOf<T> | EntityType<any, T>,
    modelKey: EntityKeyOf<T>,
    ctx: StateContext<TData>,
    key: keyof TData | string,
    url?: string,
    queryOptions?: EntityRequestOverrides,
  ): Observable<T> {
    const identifiable = modelClass.getIdField() != null;

    return this.of<T>(modelClass)
      .fetch(modelKey, { url, urlRoot: identifiable && !url?.endsWith(modelKey), ...queryOptions })
      .pipe(
        tap(result => {
          ctx.setState(
            produce(draft => {
              _.set(draft, identifiable ? [key, result.id] : key, result.toPOJO());
            }),
          );
        }),
      );
  }
  protected query<T extends Entity>(
    modelClass: EntityTypeOf<T>,
    ctx: StateContext<TData>,
    key: keyof TData | string,
    queryParams?: QueryParams<T> | QueryParamsLike<T>,
    queryOptions?: EntityRequestOverrides,
  ): Observable<T[]> {
    return this.of<T>(modelClass)
      .query(queryParams, queryOptions)
      .pipe(
        map(result => result.data),
        tap(models => {
          ctx.setState(
            produce(draft => {
              _.set(draft, key, DataUtils.convert(models));
            }),
          );
        }),
      );
  }
  /**
   * Делает запрос на получение значений справочника с сортировкой по настройкам модели.
   */
  protected queryDictionary<T extends Entity>(
    modelClass: EntityTypeOf<T>,
    ctx: StateContext<TData>,
    key: keyof TData | string,
    queryParams?: QueryParams | QueryParamsLike,
    queryOptions?: EntityRequestOverrides,
  ): Observable<T[]> {
    const cache = _.get(ctx.getState(), key) as T[];

    if (cache instanceof Array && cache.length) {
      return of(cache);
    }

    const querySorted = QueryParams.from(queryParams);

    /*if (!querySorted.hasSort()) {
      querySorted.setSort(EntityUtils.generateSort(modelClass, isDevMode()));
    }*/

    return this.query(modelClass, ctx, key, querySorted, queryOptions);
  }

  /**
   * Делает запрос на сохранение модели.
   *
   * Сохраняет полученный объект в {@link ctx} по ключу {@link key}`[ID]`.
   *
   * Если задан {@link url} запроса и значение оканчивается идентификатором модели, то параметр
   * {@link EntityRequestOverrides.urlRoot} по умолчанию устанавливается равным `true`.
   */
  protected save<T extends Entity>(
    model: T,
    ctx: StateContext<TData>,
    key: keyof TData | string,
    url?: string,
    queryOptions?: EntityRequestOverrides,
  ): Observable<T> {
    const identifiable = model.hasIdField();

    return this.of(model.getClass())
      .save(model, { url, urlRoot: identifiable && !url?.endsWith(model.id), ...queryOptions })
      .pipe(
        tap(result => {
          ctx.setState(
            produce(draft => {
              _.set(draft, identifiable ? [key, result.id] : key, result.toPOJO());
            }),
          );
        }),
      );
  }
}
